import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaExclamationTriangle, FaFilter, FaSearch, FaSyncAlt } from 'react-icons/fa';
import api from '../api';

const directionLabels = {
  INBOUND: 'Recebimento',
  OUTBOUND: 'Envio',
  CONFIGURATION: 'Configuração',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('pt-BR');
};

const formatCpf = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length !== 11) return value || '—';
  return digits.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

export default function IntegrationLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('');
  const [source, setSource] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/student-management/integration-sync/issues', {
        skipGlobalErrorModal: true,
      });
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setItems([]);
      setError(requestError?.response?.data?.error || 'Não foi possível carregar os logs de integração.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sources = useMemo(
    () => [...new Set(items.map((item) => String(item?.sourceKey || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items],
  );

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      if (direction && item?.direction !== direction) return false;
      if (source && String(item?.sourceKey || '') !== source) return false;
      if (!term) return true;
      return [item?.sourceKey, item?.cpf, item?.courseId, item?.reason, item?.issueKey, directionLabels[item?.direction]]
        .some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [items, search, direction, source]);

  const inboundCount = items.filter((item) => item?.direction === 'INBOUND').length;
  const outboundCount = items.filter((item) => item?.direction === 'OUTBOUND').length;
  const configurationCount = items.filter((item) => item?.direction === 'CONFIGURATION').length;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Integrações</p>
          <h1 className="mt-1 flex items-center gap-3 text-3xl font-semibold text-slate-900">
            <FaExclamationTriangle className="text-amber-500" /> Logs de Integração
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            Inconsistências e falhas de sincronização.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-wait disabled:opacity-60"
        >
          <FaSyncAlt className={loading ? 'animate-spin' : ''} /> {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-red-100 bg-red-50 p-4"><span className="text-xs font-semibold uppercase tracking-wide text-red-500">Inconsistências abertas</span><strong className="mt-2 block text-3xl text-red-700">{items.length}</strong></div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4"><span className="text-xs font-semibold uppercase tracking-wide text-amber-600">Recebimento</span><strong className="mt-2 block text-3xl text-amber-700">{inboundCount}</strong></div>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><span className="text-xs font-semibold uppercase tracking-wide text-blue-500">Envio</span><strong className="mt-2 block text-3xl text-blue-700">{outboundCount}</strong></div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Configuração</span><strong className="mt-2 block text-3xl text-slate-700">{configurationCount}</strong></div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><FaFilter className="text-blue-600" /> Filtrar inconsistências</div>
        <div className="grid gap-3 md:grid-cols-[minmax(260px,1.4fr)_1fr_1fr]">
          <label className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por sistema, CPF, curso ou erro" className="w-full rounded-xl border border-slate-200 py-2.5 pl-10 pr-3 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
          </label>
          <select value={direction} onChange={(event) => setDirection(event.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
            <option value="">Todos os tipos</option>
            <option value="INBOUND">Recebimento</option>
            <option value="OUTBOUND">Envio</option>
            <option value="CONFIGURATION">Configuração</option>
          </select>
          <select value={source} onChange={(event) => setSource(event.target.value)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
            <option value="">Todos os sistemas</option>
            {sources.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>
      </section>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-medium text-red-700">{error}</div> : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div><strong className="text-slate-900">Dados com inconsistência</strong><p className="text-xs text-slate-500">{filteredItems.length} registro(s) encontrado(s)</p></div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Carregando logs de integração...</div>
        ) : filteredItems.length ? (
          <div className="divide-y divide-slate-100">
            {filteredItems.map((item) => (
              <article key={item.issueKey} className="grid gap-4 p-5 lg:grid-cols-[220px_1fr]">
                <div className="space-y-2 text-sm">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${item.direction === 'INBOUND' ? 'bg-amber-100 text-amber-800' : item.direction === 'OUTBOUND' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>{directionLabels[item.direction] || item.direction || 'Integração'}</span>
                  <div><span className="block text-xs uppercase tracking-wide text-slate-400">Sistema</span><strong className="text-slate-800">{item.sourceKey || 'Não informado'}</strong></div>
                  <div><span className="block text-xs uppercase tracking-wide text-slate-400">Última ocorrência</span><span className="text-slate-700">{formatDateTime(item.updatedAt || item.occurredAt)}</span></div>
                </div>
                <div className="space-y-4">
                  <div className="rounded-xl border border-red-100 bg-red-50 p-4"><span className="text-xs font-semibold uppercase tracking-wide text-red-500">Erro encontrado</span><p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-red-800">{item.reason || 'Falha de integração sem motivo informado.'}</p></div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-xl bg-slate-50 p-3"><span className="block text-xs uppercase tracking-wide text-slate-400">CPF relacionado</span><strong className="text-sm text-slate-800">{formatCpf(item.cpf)}</strong></div>
                    <div className="rounded-xl bg-slate-50 p-3"><span className="block text-xs uppercase tracking-wide text-slate-400">Curso relacionado</span><strong className="text-sm text-slate-800">{item.courseId || '—'}</strong></div>
                    <div className="rounded-xl bg-slate-50 p-3"><span className="block text-xs uppercase tracking-wide text-slate-400">Primeira ocorrência</span><strong className="text-sm text-slate-800">{formatDateTime(item.occurredAt)}</strong></div>
                    <div className="rounded-xl bg-slate-50 p-3"><span className="block text-xs uppercase tracking-wide text-slate-400">Identificador</span><strong className="break-all text-xs text-slate-700">{item.issueKey || '—'}</strong></div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="p-10 text-center"><strong className="text-emerald-700">Nenhuma inconsistência encontrada com os filtros atuais.</strong><p className="mt-1 text-sm text-slate-500">Quando uma integração apresentar erro, o registro aparecerá nesta página.</p></div>
        )}
      </section>
    </div>
  );
}
